import type { FaseOS, OrdemServico } from '../types/ordemServico';

export type KanbanColumnId =
  | 'pendentes'
  | 'expedicao'
  | 'remocao'
  | 'clinica'
  | 'velorio'
  | 'sepultamento';

export interface KanbanColumnDef {
  id: KanbanColumnId;
  title: string;
  subtitle: string;
  headerClass: string;
}

export const KANBAN_COLUMNS: KanbanColumnDef[] = [
  {
    id: 'pendentes',
    title: 'Pendentes',
    subtitle: 'Aguardando importação',
    headerClass: 'bg-yellow-100 border-yellow-300 text-yellow-900'
  },
  {
    id: 'expedicao',
    title: 'Expedição',
    subtitle: 'Monitoramento, nenhuma fase iniciada',
    headerClass: 'bg-slate-100 border-slate-300 text-slate-900'
  },
  {
    id: 'remocao',
    title: 'Remoção',
    subtitle: 'Remoção iniciada',
    headerClass: 'bg-blue-100 border-blue-300 text-blue-900'
  },
  {
    id: 'clinica',
    title: 'Clínica',
    subtitle: 'Clínica iniciada',
    headerClass: 'bg-green-100 border-green-300 text-green-900'
  },
  {
    id: 'velorio',
    title: 'Velório',
    subtitle: 'Velório iniciado',
    headerClass: 'bg-purple-100 border-purple-300 text-purple-900'
  },
  {
    id: 'sepultamento',
    title: 'Sepultamento',
    subtitle: 'Sepultamento iniciado',
    headerClass: 'bg-gray-200 border-gray-400 text-gray-900'
  }
];

const FASE_PARA_COLUNA: Record<string, KanbanColumnId> = {
  Remoção: 'remocao',
  Clínica: 'clinica',
  Velório: 'velorio',
  Sepultamento: 'sepultamento'
};

const parseHorario = (horario?: string | null): { horas: number; minutos: number } | null => {
  if (!horario) return null;

  let horarioFormatado = horario.trim();
  if (!horarioFormatado.includes(':')) {
    if (horarioFormatado.length === 4 && /^\d{4}$/.test(horarioFormatado)) {
      horarioFormatado = `${horarioFormatado.substring(0, 2)}:${horarioFormatado.substring(2, 4)}`;
    } else {
      return null;
    }
  }

  const [horas, minutos] = horarioFormatado.split(':').map((part) => parseInt(part, 10));
  if (Number.isNaN(horas) || Number.isNaN(minutos)) return null;

  return { horas, minutos };
};

export const sepultamentoHorarioPassou = (fase?: FaseOS | null, now = new Date()): boolean => {
  if (!fase?.data_fase) return false;

  let data = fase.data_fase;
  if (typeof data === 'string') {
    data = data.split('T')[0];
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(data))) return false;

  const horario = parseHorario(fase.horario);
  const horas = horario ? String(horario.horas).padStart(2, '0') : '00';
  const minutos = horario ? String(horario.minutos).padStart(2, '0') : '00';
  const dataHora = new Date(`${data}T${horas}:${minutos}:00`);

  if (Number.isNaN(dataHora.getTime())) return false;

  return now >= dataHora;
};

export const isMesmoLocalVelorioSepultamento = (os: OrdemServico): boolean =>
  Boolean(os.velorio_sepultamento_mesmo_local);

export const faseFoiIniciada = (fase?: FaseOS | null): boolean => {
  if (!fase) return false;
  return (
    fase.status_fase === 'Em Rota' ||
    fase.status_fase === 'Concluída' ||
    !!fase.timestamp_inicio_rota
  );
};

export const findFasePorNome = (os: OrdemServico, nome: string): FaseOS | undefined =>
  os.fases?.find((f) => f.nome_fase === nome);

/** OS sai do kanban quando sepultamento concluído — ou, no mesmo local, ao atingir horário do sepultamento. */
export const osSaiuDoKanban = (os: OrdemServico): boolean => {
  if (os.isSisfuner) return false;

  const velorio = findFasePorNome(os, 'Velório');
  const sepultamento = findFasePorNome(os, 'Sepultamento');

  if (isMesmoLocalVelorioSepultamento(os)) {
    if (velorio?.status_fase === 'Concluída' && sepultamentoHorarioPassou(sepultamento)) {
      return true;
    }
    return false;
  }

  if (sepultamento?.status_fase === 'Concluída') return true;

  return os.status_geral === 'Concluída';
};

export interface KanbanPlacementInfo {
  coluna: KanbanColumnId | null;
  motivo: string;
  oculta: boolean;
}

const getFaseAtivaMaisAvancada = (
  os: OrdemServico
): { fase: FaseOS; coluna: KanbanColumnId } | null => {
  const fasesOrdenadas = [...(os.fases || [])].sort((a, b) => a.ordem - b.ordem);

  let resultado: { fase: FaseOS; coluna: KanbanColumnId } | null = null;

  for (const fase of fasesOrdenadas) {
    const coluna = FASE_PARA_COLUNA[fase.nome_fase];
    if (!coluna || !faseFoiIniciada(fase)) continue;
    resultado = { fase, coluna };
  }

  return resultado;
};

const motivoFaseAtiva = (fase: FaseOS): string => {
  if (fase.status_fase === 'Em Rota') {
    return `${fase.nome_fase} em andamento — permanece até a próxima fase iniciar ou concluir sepultamento`;
  }
  if (fase.status_fase === 'Concluída') {
    return `${fase.nome_fase} concluída — aguardando próxima fase ou conclusão do sepultamento`;
  }
  return `${fase.nome_fase} ${fase.status_fase}`;
};

/** Explica em qual coluna a OS cai e por quê (útil para diagnóstico). */
export const explainKanbanPlacement = (os: OrdemServico): KanbanPlacementInfo => {
  if (os.isSisfuner) {
    return { coluna: 'pendentes', oculta: false, motivo: 'OS ainda não importada do Sisfuner' };
  }

  if (osSaiuDoKanban(os)) {
    return { coluna: null, oculta: true, motivo: 'Sepultamento concluído / horário atingido' };
  }

  const velorio = findFasePorNome(os, 'Velório');
  const sepultamento = findFasePorNome(os, 'Sepultamento');

  if (isMesmoLocalVelorioSepultamento(os) && velorio?.status_fase === 'Concluída') {
    if (sepultamentoHorarioPassou(sepultamento)) {
      return { coluna: null, oculta: true, motivo: 'Horário do sepultamento atingido (mesmo local do velório)' };
    }

    return {
      coluna: 'velorio',
      oculta: false,
      motivo: 'Velório concluído — aguardando horário do sepultamento (mesmo local)'
    };
  }

  const faseAtiva = getFaseAtivaMaisAvancada(os);

  if (!faseAtiva) {
    const fasesCadastradas =
      os.fases?.length > 0
        ? os.fases
            .sort((a, b) => a.ordem - b.ordem)
            .map((f) => f.nome_fase)
            .join(', ')
        : 'nenhuma fase cadastrada';

    return {
      coluna: 'expedicao',
      oculta: false,
      motivo: `Nenhuma fase iniciada (${fasesCadastradas}) — aguardando motorista`
    };
  }

  return {
    coluna: faseAtiva.coluna,
    oculta: false,
    motivo: motivoFaseAtiva(faseAtiva.fase)
  };
};

export const getKanbanColumn = (os: OrdemServico): KanbanColumnId | null => {
  const info = explainKanbanPlacement(os);
  return info.oculta ? null : info.coluna;
};

export const getFaseAtivaColuna = (os: OrdemServico, columnId: KanbanColumnId): FaseOS | null => {
  if (columnId === 'pendentes' || columnId === 'expedicao') return null;

  const map: Record<KanbanColumnId, string> = {
    pendentes: '',
    expedicao: '',
    remocao: 'Remoção',
    clinica: 'Clínica',
    velorio: 'Velório',
    sepultamento: 'Sepultamento'
  };

  const nome = map[columnId];
  return findFasePorNome(os, nome) || null;
};

export const agruparOsPorColuna = (
  ordens: OrdemServico[]
): Record<KanbanColumnId, OrdemServico[]> => {
  const grupos: Record<KanbanColumnId, OrdemServico[]> = {
    pendentes: [],
    expedicao: [],
    remocao: [],
    clinica: [],
    velorio: [],
    sepultamento: []
  };

  ordens.forEach((os) => {
    const coluna = getKanbanColumn(os);
    if (coluna) grupos[coluna].push(os);
  });

  return grupos;
};

export const getOsKey = (os: OrdemServico): string =>
  os.isSisfuner ? `sisfuner-${os._DADOSOS_NumOrdem}` : `nexus-${os.id}`;
